import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import { Children, useEffect, useState } from 'react';
import { isFragment } from 'react-is';
import { useLocation } from 'react-router-dom';
import {
  ActionModeProvider,
  type ActionMode,
} from 'src/components/action-mode';
import { Button } from 'src/components/buttons-and-actions/button';
import { useMeasure } from 'src/hooks/use-measure';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';

const actionMode: ActionMode = { mode: 'tab' };

type TabsProps = {
  actions?: ReactNode;
} & ComponentPropsWithoutRef<'div'>;

const buttonClassNames = cn(
  `
    absolute -top-2xs hidden h-full cursor-pointer px-md text-foreground-default
    opacity-0 transition-all transition-discrete duration-150
    hover:text-foreground-default!
  `
);

const OldTabs: FC<TabsProps> = ({ actions, ...props }) => {
  return (
    <div
      {...props}
      className={cn(
        `
          grid grid-flow-row items-center justify-between gap-md
          md:grid-flow-col
          legacy:gap-[1rem]
        `,
        props.className
      )}
    >
      <div
        className={`
          inline-grid grid-flow-row items-center gap-2xl
          md:grid-flow-col
          legacy:gap-[2.5rem]
        `}
      >
        <ActionModeProvider value={actionMode}>
          {Children.map(props.children, function recursive(child): ReactNode {
            if (isFragment(child)) {
              return Children.map(child.props.children, recursive);
            } else if (child) {
              return child;
            } else {
              return null;
            }
          })}
        </ActionModeProvider>
      </div>

      {actions}
    </div>
  );
};

const NewTabs: FC<TabsProps> = ({ actions, ...props }) => {
  const [setContainerRef, containerBounds, containerRef] =
    useMeasure<HTMLDivElement>();

  const { pathname } = useLocation();

  useEffect(() => {
    containerRef?.querySelector('[data-active="true"]')?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [pathname, containerRef]);

  const [{ scrollLeft, scrollWidth, clientWidth }, setScrollState] = useState({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });

  useEffect(() => {
    setScrollState({
      scrollLeft: containerRef?.scrollLeft ?? 0,
      scrollWidth: containerRef?.scrollWidth ?? 0,
      clientWidth: containerRef?.clientWidth ?? 0,
    });
  }, [containerBounds, containerRef]);

  const canScrollLeft = scrollLeft > 0;
  const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;

  return (
    <div
      {...props}
      className={cn(
        `flex w-full flex-row flex-wrap justify-between gap-y-lg`,
        props.className
      )}
    >
      <div
        className={cn(`
          relative w-0 flex-1 overflow-hidden border-b-1
          border-b-border-surface-minimal
        `)}
      >
        <div
          ref={setContainerRef}
          onScroll={(event) =>
            setScrollState({
              scrollLeft: event.currentTarget.scrollLeft,
              scrollWidth: event.currentTarget.scrollWidth,
              clientWidth: event.currentTarget.clientWidth,
            })
          }
          className={cn(
            `no-scrollbar overflow-x-auto overflow-y-hidden pb-px`,
            // Hack to align the border bottom of the tab item since the container's border is somewhere else due to the temporary inclusion of `actions` prop and the nature of how this component was built. However this will be removed once the clean up is done
            `-mb-px`,
            canScrollLeft &&
              `mask-l-from-background-surface-selected mask-l-from-80%`,
            canScrollRight &&
              `mask-r-from-background-surface-selected mask-r-from-80%`
          )}
        >
          <div
            className={cn(`
              flex flex-nowrap gap-px
              [&>*]:flex-shrink-0
            `)}
          >
            <ActionModeProvider value={actionMode}>
              {Children.map(
                props.children,
                function recursive(child): ReactNode {
                  if (isFragment(child)) {
                    return Children.map(child.props.children, recursive);
                  } else if (child) {
                    return child;
                  } else {
                    return null;
                  }
                }
              )}
            </ActionModeProvider>
          </div>
        </div>

        <Button
          mode="icon"
          aria-hidden
          className={cn(
            buttonClassNames,
            `left-0`,
            canScrollLeft && `block opacity-100`
          )}
          onClick={() => {
            containerRef?.scrollBy({
              left: -containerRef.getBoundingClientRect().width / 2,
              behavior: 'smooth',
            });
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>

        <Button
          mode="icon"
          aria-hidden
          tabIndex={-1}
          className={cn(
            buttonClassNames,
            `right-0`,
            canScrollRight && `block opacity-100`
          )}
          onClick={() => {
            containerRef?.scrollBy({
              left: containerRef.getBoundingClientRect().width / 2,
              behavior: 'smooth',
            });
          }}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </div>

      <div>{actions}</div>
    </div>
  );
};

export const Tabs: FC<TabsProps> = (props) => {
  const isNewDesignActive = useIsNewDesignActive();
  return isNewDesignActive ? <NewTabs {...props} /> : <OldTabs {...props} />;
};
